import React, { useState, useMemo } from 'react';
import { 
  Calculator, 
  ArrowRight, 
  AlertTriangle, 
  CheckCircle2, 
  ChevronDown, 
  ChevronUp, 
  Building2, 
  Scale, 
  Zap, 
  Database,
  HelpCircle
} from 'lucide-react';
import { calculateBreakEven } from '../lib/breakEvenEngine';

interface PostCalculationActionHubProps {
  tab: 'quick' | 'reforma' | 'audit';
  unitPrice: number;
  unitContributionMargin: number;
  formatBRL: (val: number) => string;
  formatPct: (val: number) => string;
  onOpenCatalogModal: () => void;
  onOpenTaxDiagnosticModal: () => void;
}

export const PostCalculationActionHub: React.FC<PostCalculationActionHubProps> = ({
  tab,
  unitPrice,
  unitContributionMargin,
  formatBRL,
  formatPct,
  onOpenCatalogModal,
  onOpenTaxDiagnosticModal,
}) => {
  const [isBreakEvenOpen, setIsBreakEvenOpen] = useState(false);
  const [fixedCostsInput, setFixedCostsInput] = useState<string>('4000');
  const [workingDaysInput, setWorkingDaysInput] = useState<string>('22');

  const fixedCostsNum = useMemo(() => {
    const clean = fixedCostsInput.replace(/[^\d.,]/g, '').replace(',', '.');
    const val = parseFloat(clean);
    return isNaN(val) ? 0 : val;
  }, [fixedCostsInput]);

  const workingDaysNum = useMemo(() => {
    const val = parseInt(workingDaysInput, 10);
    return isNaN(val) || val <= 0 ? 22 : val;
  }, [workingDaysInput]);

  const breakEvenResult = useMemo(() => {
    return calculateBreakEven(
      {
        fixedCosts: fixedCostsNum,
        unitContributionMargin,
        workingDaysPerMonth: workingDaysNum,
      },
      unitPrice
    );
  }, [fixedCostsNum, unitContributionMargin, workingDaysNum, unitPrice]);

  return (
    <div className="space-y-6 pt-2">
      
      {/* ========================================================================= */}
      {/* 1. CTA PRIMÁRIO: PONTO DE EQUILÍBRIO (Atrito Zero, Entrega de Valor Imediata) */}
      {/* ========================================================================= */}
      <div className="bg-white rounded-3xl border-2 border-emerald-500/30 p-5 sm:p-6 shadow-md transition-all hover:border-emerald-500/50">
        <button
          type="button"
          onClick={() => setIsBreakEvenOpen(!isBreakEvenOpen)}
          className="w-full flex items-center justify-between gap-3 text-left group cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
              <Calculator className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <span className="inline-block text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md mb-0.5">
                Diagnóstico de Caixa Imediato
              </span>
              <h4 className="text-sm sm:text-base font-black text-slate-900 group-hover:text-emerald-700 transition-colors">
                Descobrir quantas vendas preciso fazer para cobrir minhas contas
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Calcule o Ponto de Equilíbrio mensal a partir da sobra de <strong>{formatBRL(unitContributionMargin)}</strong> por unidade
              </p>
            </div>
          </div>
          <div className="p-2 rounded-xl bg-slate-100 text-slate-600 group-hover:bg-emerald-100 group-hover:text-emerald-800 transition-colors">
            {isBreakEvenOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>

        {/* Gaveta Inline Expansível */}
        {isBreakEvenOpen && (
          <div className="mt-5 pt-5 border-t border-slate-100 space-y-4 animate-in fade-in duration-150">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Total de Contas Fixas no Mês (R$)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs font-bold text-slate-400">R$</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={fixedCostsInput}
                    onChange={(e) => setFixedCostsInput(e.target.value)}
                    placeholder="Ex: 4000"
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <span className="text-[10px] text-slate-400 mt-1 block">
                  Aluguel, equipe, contador, luz, internet e seu pró-labore.
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Dias Úteis de Operação no Mês
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={workingDaysInput}
                  onChange={(e) => setWorkingDaysInput(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-[10px] text-slate-400 mt-1 block">
                  Geralmente 22 dias úteis (segunda a sexta).
                </span>
              </div>
            </div>

            {/* Resultado do Ponto de Equilíbrio */}
            {breakEvenResult.isValid ? (
              <div className="p-4 bg-emerald-50/80 border border-emerald-200 rounded-2xl space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-xs font-black uppercase tracking-wider text-emerald-900">
                      Sua Meta Mínima de Faturamento e Vendas
                    </h5>
                    <p className="text-xs text-emerald-950 mt-1 leading-relaxed">
                      Você precisa vender no mínimo <strong>{breakEvenResult.breakEvenUnits} unidades</strong> deste produto por mês (cerca de <strong>{breakEvenResult.dailyUnits} unidades por dia útil</strong>) apenas para pagar as contas básicas da sua empresa e não ter prejuízo.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 border-t border-emerald-200/60 text-xs">
                  <div>
                    <span className="text-[10px] text-emerald-800 font-medium block">Faturamento Mínimo:</span>
                    <strong className="text-slate-900 font-black">{formatBRL(breakEvenResult.revenueAtBreakEven)}</strong>
                  </div>
                  <div>
                    <span className="text-[10px] text-emerald-800 font-medium block">Meta Mensal:</span>
                    <strong className="text-emerald-800 font-black">{breakEvenResult.breakEvenUnits} un</strong>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <span className="text-[10px] text-emerald-800 font-medium block">Ritmo Diário:</span>
                    <strong className="text-emerald-800 font-black">{breakEvenResult.dailyUnits} un/dia</strong>
                  </div>
                </div>

                <div className="pt-2 border-t border-emerald-200/60 flex flex-col sm:flex-row items-center justify-between gap-2">
                  <span className="text-[11px] text-emerald-900 font-medium text-center sm:text-left">
                    A partir da <strong>{breakEvenResult.breakEvenUnits + 1}ª unidade</strong>, a sua empresa começa a ter lucro líquido real.
                  </span>
                  <button
                    type="button"
                    onClick={onOpenCatalogModal}
                    className="shrink-0 text-xs font-bold text-emerald-800 hover:text-emerald-950 hover:underline flex items-center gap-1"
                  >
                    <span>Auditar meus outros produtos</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl space-y-2 text-xs text-amber-950">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-amber-900 font-bold mb-0.5">
                      Ponto de Equilíbrio Inviável no Cenário Atual
                    </strong>
                    <p className="leading-relaxed">
                      {breakEvenResult.message}
                    </p>
                  </div>
                </div>

                {unitContributionMargin <= 0 && (
                  <div className="pt-2 border-t border-amber-200/60 flex items-center justify-between">
                    <span className="text-[11px] text-amber-900">
                      Precisa de apoio para reestruturar suas margens ou regime tributário?
                    </span>
                    <button
                      type="button"
                      onClick={onOpenTaxDiagnosticModal}
                      className="px-3 py-1.5 bg-amber-700 hover:bg-amber-800 text-white font-bold rounded-lg text-xs transition-colors shrink-0"
                    >
                      Ver Diagnóstico Tributário
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* 2. CTA SECUNDÁRIO: AUDITAR CATÁLOGO COMPLETO (Alto Valor B2B) */}
      {/* ========================================================================= */}
      <div className="bg-linear-to-br from-emerald-600 to-teal-700 text-white p-6 sm:p-7 rounded-3xl text-center flex flex-col items-center shadow-lg shadow-emerald-600/20">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold mb-3">
          <Database className="w-3.5 h-3.5 text-emerald-200" />
          Para empresas com catálogo amplo ou ERP
        </span>
        <h3 className="text-lg font-black text-white mb-2">
          {tab === 'reforma'
            ? 'Simular Reforma no Meu Catálogo Completo'
            : tab === 'audit'
            ? 'Proteja seus lucros contra prejuízos invisíveis'
            : 'Auditar Todo o Catálogo de Produtos'}
        </h3>
        <p className="text-xs text-emerald-100 mb-5 max-w-md leading-relaxed">
          {tab === 'reforma'
            ? 'Analise centenas de SKUs de uma só vez via planilha CSV ou conector seguro para o seu ERP (Senior, Totvs, SAP ou outros).'
            : 'Identifique quais produtos da sua loja sustentam as contas e quais estão gerando perda financeira silenciosa a cada venda.'}
        </p>
        <button
          type="button"
          onClick={onOpenCatalogModal}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-emerald-950 font-black px-6 py-3 rounded-xl transition-all shadow-md text-sm hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
        >
          <span>Auditar Todo o Meu Catálogo</span>
          <ArrowRight className="w-4 h-4 text-emerald-700" />
        </button>
      </div>

      {/* ========================================================================= */}
      {/* 3. CTA TERCIÁRIO: DIAGNÓSTICO DE REGIME TRIBUTÁRIO (4Shift Gestão) */}
      {/* ========================================================================= */}
      <div className="bg-slate-50 border border-slate-200 rounded-3xl p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="space-y-1 text-center sm:text-left">
          <div className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-teal-800 bg-teal-100 px-2 py-0.5 rounded-md">
            <Scale className="w-3.5 h-3.5 text-teal-700" />
            Parceria Especializada 4Shift
          </div>
          <h4 className="text-sm font-black text-slate-900">
            Diagnóstico de Regime Tributário: Simples Nacional vs Lucro Real
          </h4>
          <p className="text-xs text-slate-500 max-w-lg leading-relaxed">
            Descubra se sua empresa está no regime certo com análise validada por contador e call de devolutiva técnica. Pagamento único, sem contrato de 12 meses.
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenTaxDiagnosticModal}
          className="shrink-0 py-2.5 px-4 bg-teal-800 hover:bg-teal-700 text-white font-bold rounded-xl text-xs transition-colors shadow-xs flex items-center gap-1.5"
        >
          <span>Conhecer Diagnóstico</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* ========================================================================= */}
      {/* 4. CTA DISCRETO: TESTAR R-MAX SAAS (Trial 14 Dias) */}
      {/* ========================================================================= */}
      <div className="text-center pt-1">
        <a
          href="/auth?mode=register"
          className="text-xs text-slate-500 hover:text-slate-800 transition-colors inline-flex items-center gap-1 font-medium"
        >
          <span>Quer automatizar fichas técnicas e orçamentos comerciais?</span>
          <strong className="text-emerald-700 hover:underline">Testar R-MAX Grátis por 14 Dias →</strong>
        </a>
      </div>

    </div>
  );
};
